import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { Form, InputGroup, Button } from "react-bootstrap";
import ErrorAlert from "../Alerts/ErrorAlert";
import SuccessAlert from "../Alerts/SuccessAlert";

const DEFAULT_CENTER = { lat: 8.9824, lng: -79.5199 };
const DEFAULT_COLORS = ["#1976D2", "#43A047", "#FB8C00", "#8E24AA", "#E53935", "#00897B", "#6D4C41", "#3949AB"];

const nextColor = (zones) => DEFAULT_COLORS[zones.length % DEFAULT_COLORS.length];

/* KML color format is aabbggrr -> CSS #rrggbb */
const kmlColorToHex = (kmlColor) => {
    if (!kmlColor || kmlColor.length !== 8) return null;
    const bb = kmlColor.slice(2, 4);
    const gg = kmlColor.slice(4, 6);
    const rr = kmlColor.slice(6, 8);
    return `#${rr}${gg}${bb}`;
};

const parseKmlCoordinates = (text) => (
    text
        .trim()
        .split(/\s+/)
        .map((triplet) => {
            const [lng, lat] = triplet.split(",").map(Number);
            return { lat, lng };
        })
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
);

/**
 * Parses a KML file exported from Google My Maps ("Descargar" > "Exportar a KML/KMZ",
 * choosing KML rather than KMZ) into our zones shape: { name, price, color, polygon }.
 * Zone names of the form "Nombre $ Precio" (as My Maps shows them) are split into
 * name + price automatically; otherwise the price defaults to 0 for manual editing.
 */
const parseKmlZones = (kmlText) => {
    const doc = new DOMParser().parseFromString(kmlText, "text/xml");
    if (doc.querySelector("parsererror")) {
        throw new Error("invalid KML");
    }

    const styleColors = {};
    doc.querySelectorAll("Style").forEach((style) => {
        const id = style.getAttribute("id");
        const colorEl = style.querySelector("PolyStyle > color");
        if (id && colorEl) {
            const hex = kmlColorToHex(colorEl.textContent.trim());
            if (hex) styleColors[id] = hex;
        }
    });

    const styleMapNormal = {};
    doc.querySelectorAll("StyleMap").forEach((map) => {
        const id = map.getAttribute("id");
        map.querySelectorAll("Pair").forEach((pair) => {
            const key = pair.querySelector("key")?.textContent?.trim();
            const styleUrl = pair.querySelector("styleUrl")?.textContent?.trim()?.replace("#", "");
            if (key === "normal" && id && styleUrl) styleMapNormal[id] = styleUrl;
        });
    });

    let colorIndex = 0;
    const resolveColor = (styleUrlRaw) => {
        const id = (styleUrlRaw || "").replace("#", "");
        const resolvedId = styleMapNormal[id] || id;
        return styleColors[resolvedId] || DEFAULT_COLORS[colorIndex++ % DEFAULT_COLORS.length];
    };

    const zones = [];

    doc.querySelectorAll("Placemark").forEach((placemark) => {
        const rawName = placemark.querySelector("name")?.textContent?.trim() || "Zona sin nombre";
        const styleUrl = placemark.querySelector("styleUrl")?.textContent?.trim();

        const priceMatch = rawName.match(/^(.*?)\s*\$\s*([\d.,]+)\s*$/);
        const zoneName = priceMatch ? priceMatch[1].trim() : rawName;
        const price = priceMatch ? Number(priceMatch[2].replace(",", ".")) : 0;
        const color = resolveColor(styleUrl);

        placemark.querySelectorAll("Polygon").forEach((polygonEl) => {
            const coordsEl = polygonEl.querySelector("outerBoundaryIs LinearRing coordinates");
            if (!coordsEl) return;

            const polygon = parseKmlCoordinates(coordsEl.textContent);
            if (polygon.length < 3) return;

            zones.push({
                id: `kml-${Date.now()}-${zones.length}`,
                name: zoneName,
                price,
                color,
                priority: 100,
                polygon,
            });
        });
    });

    return zones;
};

/* Imperative google.maps.Polygon overlay for one saved zone */
const ZonePolygon = ({ zone, editable, onEdit, onClick }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || !window.google) return;

        const polygon = new window.google.maps.Polygon({
            paths: zone.polygon,
            strokeColor: zone.color,
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: zone.color,
            fillOpacity: 0.25,
            editable: !!editable,
            map,
        });

        const clickListener = polygon.addListener("click", () => onClick?.(zone));
        const pathListeners = [];

        if (editable) {
            const path = polygon.getPath();
            const handlePathChange = () => {
                const points = [];
                path.forEach((latLng) => points.push({ lat: latLng.lat(), lng: latLng.lng() }));
                onEdit?.(points);
            };
            pathListeners.push(window.google.maps.event.addListener(path, "set_at", handlePathChange));
            pathListeners.push(window.google.maps.event.addListener(path, "insert_at", handlePathChange));
            pathListeners.push(window.google.maps.event.addListener(path, "remove_at", handlePathChange));
        }

        return () => {
            window.google.maps.event.removeListener(clickListener);
            pathListeners.forEach((l) => window.google.maps.event.removeListener(l));
            polygon.setMap(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, zone.id, zone.color, editable]);

    return null;
};

/* Live preview of the polygon being drawn (click-to-add-vertex), no drawing library needed */
const DraftOverlay = ({ points }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || !window.google || points.length === 0) return;

        const isPolygon = points.length >= 3;
        const shape = isPolygon
            ? new window.google.maps.Polygon({
                paths: points,
                strokeColor: "#1B3A6B",
                strokeWeight: 2,
                fillColor: "#1B3A6B",
                fillOpacity: 0.2,
                clickable: false,
                map,
            })
            : new window.google.maps.Polyline({
                path: points,
                strokeColor: "#1B3A6B",
                strokeWeight: 2,
                clickable: false,
                map,
            });

        const markers = points.map((p) => new window.google.maps.Marker({
            position: p,
            map,
            icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 5,
                fillColor: "#1B3A6B",
                fillOpacity: 1,
                strokeColor: "#fff",
                strokeWeight: 1.5,
            },
        }));

        return () => {
            shape.setMap(null);
            markers.forEach((m) => m.setMap(null));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, points]);

    return null;
};

/**
 * Editor for the coverage zones (polygons) of a fee_type = 'zone' delivery rule.
 * `zones` / `onChange` follow controlled-component convention, like the rest of the wizard form.
 *
 * Polygons are drawn by clicking vertices directly on the map (rather than via
 * google.maps.drawing.DrawingManager, which Google deprecated/removed from newer
 * unversioned loads of the Maps JavaScript API).
 */
const ZoneMapEditor = ({ zones = [], onChange, branchCenter }) => {
    const [drawing, setDrawing] = useState(false);
    const [draftPoints, setDraftPoints] = useState([]);
    const [pendingPolygon, setPendingPolygon] = useState(null);
    const [editingZoneId, setEditingZoneId] = useState(null);
    const [form, setForm] = useState({ name: "", price: "", color: DEFAULT_COLORS[0] });
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const handleImportClick = () => fileInputRef.current?.click();

    const handleKmlFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        try {
            setImporting(true);
            const text = await file.text();
            const imported = parseKmlZones(text);

            if (imported.length === 0) {
                ErrorAlert("No se encontraron zonas (polígonos) en el archivo.", "Sin datos", "warning");
                return;
            }

            onChange([...zones, ...imported]);
            SuccessAlert(`Se importaron ${imported.length} zona${imported.length === 1 ? "" : "s"} desde el KML.`);
        } catch (err) {
            ErrorAlert(
                "No se pudo leer el archivo. Verifica que sea un .kml exportado de Google My Maps (Menú ⋮ → Descargar → Exportar a KML/KMZ, eligiendo KML).",
                "Error al importar",
                "error"
            );
        } finally {
            setImporting(false);
        }
    };

    const startDrawing = () => {
        setPendingPolygon(null);
        setEditingZoneId(null);
        setForm({ name: "", price: "", color: nextColor(zones) });
        setDraftPoints([]);
        setDrawing(true);
    };

    const cancelDrawing = () => {
        setDrawing(false);
        setDraftPoints([]);
    };

    const handleMapClick = (e) => {
        if (!drawing) return;
        const latLng = e?.detail?.latLng;
        if (!latLng) return;
        setDraftPoints((prev) => [...prev, { lat: latLng.lat, lng: latLng.lng }]);
    };

    const undoLastPoint = () => {
        setDraftPoints((prev) => prev.slice(0, -1));
    };

    const finishDrawing = () => {
        if (draftPoints.length < 3) return;
        setDrawing(false);
        setPendingPolygon(draftPoints);
        setDraftPoints([]);
    };

    const cancelPending = () => {
        setPendingPolygon(null);
    };

    const savePending = () => {
        if (!form.name.trim() || form.price === "" || isNaN(form.price) || Number(form.price) < 0) return;
        const newZone = {
            id: `new-${Date.now()}`,
            name: form.name.trim(),
            price: Number(form.price),
            color: form.color,
            priority: 100,
            polygon: pendingPolygon,
        };
        onChange([...zones, newZone]);
        setPendingPolygon(null);
    };

    const updateZoneField = (id, field, value) => {
        onChange(zones.map((z) => (z.id === id ? { ...z, [field]: value } : z)));
    };

    const updateZonePolygon = (id, points) => {
        onChange(zones.map((z) => (z.id === id ? { ...z, polygon: points } : z)));
    };

    const removeZone = (id) => {
        onChange(zones.filter((z) => z.id !== id));
        if (editingZoneId === id) setEditingZoneId(null);
    };

    if (!apiKey) {
        return <div className="dm-hint">⚠️ Falta configurar REACT_APP_GOOGLE_MAPS_API_KEY para poder dibujar zonas.</div>;
    }

    return (
        <div className="dm-zone-editor">
            <div className="dm-zone-toolbar">
                {!drawing ? (
                    <div className="dm-zone-drawing-controls">
                        <button type="button" className="dm-btn-navy" onClick={startDrawing}>
                            + Nueva zona
                        </button>
                        <button type="button" className="dm-btn-ghost" onClick={handleImportClick} disabled={importing}>
                            {importing ? "Importando…" : "📥 Importar KML"}
                        </button>
                        <input
                            type="file"
                            accept=".kml"
                            ref={fileInputRef}
                            onChange={handleKmlFileChange}
                            style={{ display: "none" }}
                        />
                    </div>
                ) : (
                    <div className="dm-zone-drawing-controls">
                        <button type="button" className="dm-btn-navy" onClick={finishDrawing} disabled={draftPoints.length < 3}>
                            Finalizar zona ({draftPoints.length} punto{draftPoints.length === 1 ? "" : "s"})
                        </button>
                        <button type="button" className="dm-btn-ghost" onClick={undoLastPoint} disabled={draftPoints.length === 0}>
                            Deshacer punto
                        </button>
                        <button type="button" className="dm-btn-ghost" onClick={cancelDrawing}>
                            Cancelar
                        </button>
                    </div>
                )}
                <span className="dm-zone-count">
                    {zones.length} zona{zones.length === 1 ? "" : "s"} configurada{zones.length === 1 ? "" : "s"}
                </span>
            </div>

            {drawing && (
                <div className="dm-hint">🖊️ Haz clic en el mapa para ir agregando los vértices de la zona. Necesitas al menos 3 puntos.</div>
            )}
            {!drawing && zones.length === 0 && (
                <div className="dm-hint">📥 ¿Ya tienes las zonas dibujadas en Google My Maps? Ábrelo, ve a Menú (⋮) → Descargar → Exportar a KML/KMZ, elige <strong>KML</strong> (no KMZ) y súbelo con "Importar KML" para traer todas las zonas y precios de una vez.</div>
            )}

            <div className="dm-zone-map-wrap">
                <APIProvider apiKey={apiKey}>
                    <Map
                        defaultCenter={branchCenter || DEFAULT_CENTER}
                        defaultZoom={12}
                        style={{ width: "100%", height: "380px", borderRadius: 12 }}
                        mapTypeControl={false}
                        streetViewControl={false}
                        fullscreenControl={true}
                        onClick={handleMapClick}
                    >
                        <DraftOverlay points={draftPoints} />
                        {zones.map((zone) => (
                            <ZonePolygon
                                key={zone.id}
                                zone={zone}
                                editable={editingZoneId === zone.id}
                                onEdit={(points) => updateZonePolygon(zone.id, points)}
                                onClick={() => setEditingZoneId(zone.id)}
                            />
                        ))}
                    </Map>
                </APIProvider>
            </div>

            {pendingPolygon && (
                <div className="dm-zone-pending-form">
                    <div className="dm-hint">📍 Define el nombre y el precio para esta zona.</div>
                    <div className="dm-zone-pending-row">
                        <Form.Control
                            placeholder="Nombre de la zona (ej. Ancón)"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        <InputGroup>
                            <InputGroup.Text>$</InputGroup.Text>
                            <Form.Control
                                type="number"
                                step="0.01"
                                placeholder="Precio"
                                value={form.price}
                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                            />
                        </InputGroup>
                        <input
                            type="color"
                            value={form.color}
                            onChange={(e) => setForm({ ...form, color: e.target.value })}
                            className="dm-zone-color-input"
                            title="Color de la zona"
                        />
                        <Button variant="outline-secondary" size="sm" onClick={cancelPending}>Cancelar</Button>
                        <Button variant="primary" size="sm" onClick={savePending}>Guardar zona</Button>
                    </div>
                </div>
            )}

            {zones.length > 0 && (
                <div className="dm-zone-list">
                    {zones.map((zone) => (
                        <div key={zone.id} className="dm-zone-row">
                            <span className="dm-zone-swatch" style={{ background: zone.color }} />
                            <input
                                className="dm-zone-name-input"
                                value={zone.name}
                                onChange={(e) => updateZoneField(zone.id, "name", e.target.value)}
                            />
                            <InputGroup size="sm" className="dm-zone-price-input">
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={zone.price}
                                    onChange={(e) => updateZoneField(zone.id, "price", e.target.value)}
                                />
                            </InputGroup>
                            <button
                                type="button"
                                className="dm-btn-ghost"
                                onClick={() => setEditingZoneId(editingZoneId === zone.id ? null : zone.id)}
                            >
                                {editingZoneId === zone.id ? "Listo" : "Editar forma"}
                            </button>
                            <button type="button" className="dm-btn-del" onClick={() => removeZone(zone.id)}>Eliminar</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ZoneMapEditor;
